/**
 * Session lock overlay.
 *
 * Deliberately an **overlay, not a logout**. The session token stays valid and the routes
 * underneath stay mounted, so a false positive costs the employee a few seconds rather than an
 * in-progress loan review. That trade is the entire reason this is a lock: a control that
 * destroys work gets switched off within a week, and then protects nothing.
 *
 * Re-authentication is a live liveness check — the same challenge machinery as enrolment, one
 * challenge, server-issued in production. That is stronger than a password box here, because
 * the thing being re-established is *who is sitting in the chair*, which a password cannot
 * show. A password fallback still has to exist for anyone whose face verification fails
 * legitimately (an injury, new glasses, a broken camera); that path belongs to the portal's
 * own auth flow rather than to this component.
 *
 * **The server decides whether the lock opens, not this component.** Liveness runs here because
 * it is genuinely useful — it stops a held-up photo before a request is even made — but it cannot
 * be the thing that unlocks, because it is computed from landmarks in this page and a server told
 * "liveness passed" has been told a story it cannot check. The captured frames are submitted and
 * admin-api compares them against the enrolled template itself.
 *
 * That is also what makes the lock survive a page reload: the state lives in admin-api and the
 * console's data routes answer 423 while it is in force, so refreshing, opening a second tab, or
 * calling the API directly all hit the same refusal.
 */

import { useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import { CHALLENGE_PROMPTS, type GuardDecision, type LockReason } from '../core/index.js';
import { requestClear } from '../cv/sessionLockClient';
import { useLivenessEnrolment } from '../hooks/useLivenessEnrolment';

interface SessionLockOverlayProps {
  decision: GuardDecision;
  onUnlocked: () => void;
  /** Offered when face verification cannot succeed for a legitimate reason. */
  onUsePassword?: () => void;
}

/** How many of the captured frames are submitted for the server-side match. */
const CLEAR_FRAMES = 3;

/**
 * Re-authentication challenge, hoisted so its identity is stable across renders.
 *
 * An inline `['smile']` would be a new array each render. The hook no longer cares, but a module
 * constant is the honest way to express "this never changes" and costs nothing.
 */
const REAUTH_CHALLENGES = ['smile'] as const;

type ClearState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  /** The server refused, or could not find a usable face. The lock stands. */
  | { kind: 'refused' }
  /** Released, but without a face match — the server says why. */
  | { kind: 'unverified'; detail: string };

const REASON_HEADLINE: Readonly<Record<LockReason, string>> = {
  // Neutral wording: state the observation, not a judgement about the person.
  face_absent: 'Workstation locked — nobody detected at the camera',
  identity_mismatch: 'Workstation locked — could not confirm it is you',
  // Says what happened rather than implying the employee did something. A stopped camera is
  // usually a fault, not evasion, and the copy should not accuse anybody of the latter.
  camera_unavailable: 'Workstation locked — the camera stopped working',
  // States the observable fact and nothing more. The employee may have had a perfectly good
  // reason to be in another window, so the wording must not imply otherwise.
  console_backgrounded: 'Workstation locked — the console was left in the background',
};

/**
 * What the employee is asked to do, per situation.
 *
 * Deliberately free of numbers. The guard's evidence carries exact similarities, durations and
 * thresholds, and those belong in the audit record where an engineer can act on them — not in
 * front of the person who has just been locked out. Three reasons: the figures mean nothing
 * without the scale behind them, an employee reading "similarity 0.048" learns only that they
 * have been measured, and a displayed threshold tells anyone trying to defeat the control exactly
 * what target to aim at.
 */
const REASON_GUIDANCE: Readonly<Record<LockReason, string>> = {
  face_absent:
    'Sit back in front of the camera and verify below to carry on where you left off.',
  identity_mismatch:
    'Verify below to continue. If the camera has been showing somebody else, the account holder needs to verify.',
  camera_unavailable:
    'Reconnect or re-enable the camera, then verify below. If it cannot be fixed right now, sign in with your password instead.',
  console_backgrounded:
    'Your session is still open and nothing has been lost. Presence cannot be confirmed while the console is out of view, so access is paused rather than left open.',
};

const REASON_DETAIL: Readonly<Record<LockReason, string>> = {
  face_absent:
    'Your session is still open and nothing has been lost. Verify below to carry on where you left off.',
  identity_mismatch:
    'The face at the camera did not match this account. If this is you, verify below; the check can be affected by lighting, glasses or an unusual angle.',
  camera_unavailable:
    'Your session is still open and nothing has been lost. Presence cannot be confirmed without a working camera, so access is paused rather than left open. Reconnect or re-enable the camera and verify below — or sign in with your password if the camera cannot be fixed right now.',
  console_backgrounded:
    'Your session is still open and nothing has been lost. Verify below to carry on where you left off.',
};

export function SessionLockOverlay({
  decision,
  onUnlocked,
  onUsePassword,
}: SessionLockOverlayProps) {
  const [verifying, setVerifying] = useState(false);
  const [clearState, setClearState] = useState<ClearState>({ kind: 'idle' });

  const reason: LockReason = decision.reason ?? 'face_absent';

  const liveness = useLivenessEnrolment({
    // A single challenge: this re-establishes that a live person is here, before we spend a
    // round trip asking the server who they are.
    serverChallenges: REAUTH_CHALLENGES,
    onComplete: (frames) => {
      void (async () => {
        setClearState({ kind: 'checking' });
        // The frames that just passed liveness are the ones submitted, so the face proven live
        // is provably the face being matched — there is no window to swap one in between.
        const outcome = await requestClear(frames.slice(-CLEAR_FRAMES).map((f) => f.blob));

        if (!outcome.cleared) {
          // The server's reason and score are not carried into UI state: the employee is shown
          // generic guidance, and the specifics are already in the audit log.
          setClearState({ kind: 'refused' });
          return;
        }
        if (!outcome.verified) {
          // Released without a match. Surfaced rather than presented as a successful check.
          setClearState({ kind: 'unverified', detail: outcome.detail });
        }
        onUnlocked();
      })();
    },
  });

  const status = liveness.status;

  const begin = (): void => {
    setVerifying(true);
    setClearState({ kind: 'idle' });
    liveness.start();
  };

  const retry = (): void => {
    setClearState({ kind: 'idle' });
    liveness.retry();
  };

  // A server refusal is a failed attempt even though liveness itself succeeded, so the retry
  // button has to appear for it too.
  const canRetry = liveness.phase === 'failed' || clearState.kind === 'refused';

  return (
    <Backdrop
      open
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        bgcolor: 'rgba(9, 12, 16, 0.92)',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 520, width: '100%' }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="error" sx={{ letterSpacing: '0.08em' }}>
                Session locked
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {REASON_HEADLINE[reason]}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {REASON_DETAIL[reason]}
              </Typography>
            </Box>

            {/* Generic guidance rather than the raw evidence — see REASON_GUIDANCE. The precise
                measurements are recorded in the audit log for whoever needs to diagnose them. */}
            <Typography variant="body2" color="text.secondary">
              {REASON_GUIDANCE[reason]}
            </Typography>

            {verifying ? (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '4 / 3',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: '#0f1419',
                }}
              >
                <Box
                  component="video"
                  ref={liveness.videoRef}
                  autoPlay
                  playsInline
                  muted
                  aria-label="Camera preview for re-verification"
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: liveness.policy.presentation.mirrorDisplay
                      ? 'scaleX(-1)'
                      : 'none',
                  }}
                />
              </Box>
            ) : null}

            {verifying && status !== null ? (
              <Box>
                <Typography variant="subtitle1" align="center">
                  {status.prompt === null
                    ? 'Verifying…'
                    : CHALLENGE_PROMPTS[status.prompt.kind].title}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {status.message}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.round(
                    (status.phase === 'holding' ? status.holdProgress : status.progress) * 100,
                  )}
                  sx={{ mt: 1.5, height: 8, borderRadius: 4 }}
                />
              </Box>
            ) : null}

            {liveness.phase === 'failed' ? (
              <Alert severity="warning" variant="outlined">
                {liveness.errorMessage ??
                  status?.failureReason ??
                  'Verification did not complete.'}
              </Alert>
            ) : null}

            {clearState.kind === 'checking' ? (
              <Alert severity="info" variant="outlined">
                Checking this face against the account…
              </Alert>
            ) : null}

            {clearState.kind === 'refused' ? (
              <Alert severity="error" variant="outlined">
                A live person was confirmed, but this face does not match the account, so the
                workstation stays locked. If this is you, retry facing the camera squarely in even
                light — otherwise the account holder needs to verify, or sign in with a password.
              </Alert>
            ) : null}

            {clearState.kind === 'unverified' ? (
              <Alert severity="warning" variant="outlined">
                Unlocked, but without an identity check — {clearState.detail}. This was not a
                confirmation that the face matches the account.
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1} justifyContent="center">
              {!verifying ? (
                <Button variant="contained" onClick={begin}>
                  Verify to continue
                </Button>
              ) : null}
              {canRetry ? (
                <Button variant="contained" onClick={retry}>
                  Try again
                </Button>
              ) : null}
              {onUsePassword !== undefined ? (
                <Button variant="text" onClick={onUsePassword}>
                  Use password instead
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Backdrop>
  );
}
